import jwtDecode from 'jwt-decode';

const AUTH_SESSION_KEY = 'gestop-auth-session';

const parseStoredBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

const parseStoredJson = (key, fallback) => {
    try {
        const rawValue = localStorage.getItem(key);

        return rawValue ? JSON.parse(rawValue) : fallback;
    } catch (error) {
        return fallback;
    }
};

const normalizeSession = (session = {}) => ({
    serviceToken: session?.serviceToken || null,
    refreshToken: session?.refreshToken || null,
    keepMeLoggedIn: parseStoredBoolean(session?.keepMeLoggedIn),
    user: session?.user && typeof session.user === 'object' ? session.user : {},
    profileSettings: Array.isArray(session?.profileSettings) ? session.profileSettings : [],
    license: session?.license && typeof session.license === 'object' ? session.license : {}
});

const hasSessionData = (session = {}) =>
    Boolean(session?.serviceToken || session?.refreshToken || Object.keys(session?.user || {}).length || session?.profileSettings?.length);

export const verifyToken = (serviceToken) => {
    if (!serviceToken || typeof serviceToken !== 'string') {
        return false;
    }

    const decoded = jwtDecode(serviceToken);

    return 'exp' in decoded ? decoded.exp > Date.now() / 1000 : false;
};

export const loadAuthSession = () => {
    const storedSession = parseStoredJson(AUTH_SESSION_KEY, null);

    if (storedSession) {
        return normalizeSession(storedSession);
    }

    const legacySession = normalizeSession({
        serviceToken: localStorage.getItem('serviceToken'),
        refreshToken: localStorage.getItem('refreshToken'),
        keepMeLoggedIn: localStorage.getItem('keepMeLoggedIn'),
        user: parseStoredJson('user', {}),
        profileSettings: parseStoredJson('profileSettings', []),
        license: parseStoredJson('license', {})
    });

    return hasSessionData(legacySession) ? legacySession : null;
};

export const saveAuthSession = (session = {}) => {
    const normalizedSession = normalizeSession(session);

    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(normalizedSession));

    if (normalizedSession.serviceToken) {
        localStorage.setItem('serviceToken', normalizedSession.serviceToken);
        localStorage.setItem('tokenIsExpired', false);
    } else {
        localStorage.removeItem('serviceToken');
        localStorage.removeItem('tokenIsExpired');
    }

    if (normalizedSession.refreshToken) {
        localStorage.setItem('refreshToken', normalizedSession.refreshToken);
    } else {
        localStorage.removeItem('refreshToken');
    }

    if (normalizedSession.keepMeLoggedIn) {
        localStorage.setItem('keepMeLoggedIn', '1');
    } else {
        localStorage.removeItem('keepMeLoggedIn');
    }

    localStorage.removeItem('user');
    localStorage.removeItem('profileSettings');
    localStorage.removeItem('license');

    return normalizedSession;
};

export const clearAuthSession = () => {
    localStorage.removeItem(AUTH_SESSION_KEY);
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('tokenIsExpired');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('keepMeLoggedIn');
    localStorage.removeItem('user');
    localStorage.removeItem('profileSettings');
    localStorage.removeItem('license');
};

const parseStructuredProfileSettingValue = (fieldValue, fallbackValue) => {
    if (typeof fieldValue !== 'string') {
        return fieldValue ?? fallbackValue;
    }

    if (!fieldValue.trim()) {
        return fallbackValue;
    }

    try {
        return JSON.parse(fieldValue);
    } catch (error) {
        return fallbackValue;
    }
};

export const buildConfigFromProfileSettings = (profileSettings = []) =>
    (Array.isArray(profileSettings) ? profileSettings : []).reduce((acc, setting) => {
        let settingValue = null;

        switch (setting?.fieldDataType) {
            case 'boolean':
                settingValue = parseStoredBoolean(setting?.fieldValue);
                break;
            case 'array':
                settingValue = parseStructuredProfileSettingValue(setting?.fieldValue, []);
                break;
            case 'json':
            case 'object':
                settingValue = parseStructuredProfileSettingValue(setting?.fieldValue, null);
                break;
            default:
                settingValue = setting?.fieldValue;
        }

        if (setting?.name) {
            Object.assign(acc, { [setting.name]: settingValue });
        }

        return acc;
    }, {});
